import { useRef, useState } from 'react';
import { Textarea } from '@components/ui/textarea';
import { Label } from '@components/ui/label';
import { Button } from '@components/ui/button';
import { FileUp, X } from 'lucide-react';
import type { MaterialItem } from '@generated/types';
import { validateUploadFile } from '@generated/utils/files';

const NOTES_MAX = 2000;
const NOTES_WARN_AT = 1500;

interface UploadPanelProps {
  materials: MaterialItem[];
  files: Record<string, File[]>;
  onFilesAdd: (materialId: string, newFiles: File[]) => void;
  onFileRemove: (materialId: string, fileIndex: number) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  showErrors: boolean;
  uploadErrorIds: Set<string>;
}

export function UploadPanel({
  materials,
  files,
  onFilesAdd,
  onFileRemove,
  notes,
  onNotesChange,
  showErrors,
  uploadErrorIds,
}: UploadPanelProps) {
  if (materials.length === 0) return null;

  return (
    <div className="mt-5 rounded-lg border border-border bg-secondary/60 p-4 sm:p-5">
      <h3 className="font-[family-name:var(--font-heading)] text-sm font-medium text-foreground">Send us a spec</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Attach photos of the material or product data sheets and we&apos;ll confirm we can take it. PDF, JPG, PNG,
        WEBP or HEIC, up to 10MB each.
      </p>
      <div className="mt-4 space-y-3">
        {materials.map((m) => (
          <MaterialUploadRow
            key={m.id}
            material={m}
            files={files[m.id] ?? []}
            onFilesAdd={onFilesAdd}
            onFileRemove={onFileRemove}
            invalid={showErrors && uploadErrorIds.has(m.id)}
          />
        ))}
      </div>
      <div className="mt-4">
        <Label htmlFor="extra-notes" className="text-xs font-medium text-foreground">
          Anything else we should know
        </Label>
        <Textarea
          id="extra-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value.slice(0, NOTES_MAX))}
          maxLength={NOTES_MAX}
          rows={3}
          className="mt-1.5 bg-card"
          placeholder="Optional context for our operations team."
        />
        {notes.length > NOTES_WARN_AT ? (
          <p className={`mt-1 text-right text-xs ${notes.length >= NOTES_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
            {notes.length.toLocaleString()} / {NOTES_MAX.toLocaleString()}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function MaterialUploadRow({
  material,
  files,
  onFilesAdd,
  onFileRemove,
  invalid,
}: {
  material: MaterialItem;
  files: File[];
  onFilesAdd: (id: string, f: File[]) => void;
  onFileRemove: (id: string, idx: number) => void;
  invalid: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejectionMessages, setRejectionMessages] = useState<string[]>([]);

  function handleFiles(list: File[]) {
    const accepted: File[] = [];
    const rejections: string[] = [];
    for (const file of list) {
      const result = validateUploadFile(file);
      if (result.ok) accepted.push(file);
      else rejections.push(result.reason);
    }
    setRejectionMessages(rejections);
    if (accepted.length) onFilesAdd(material.id, accepted);
  }

  return (
    <div className={`rounded-md border p-3 ${invalid ? 'border-destructive/60 bg-destructive/5' : 'border-border bg-card'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="break-words text-sm font-medium text-foreground">{material.name}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
          <FileUp className="h-3.5 w-3.5" />
          Attach files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
          className="hidden"
          aria-label={`Attach files for ${material.name}`}
          onChange={(e) => {
            const list = e.target.files ? Array.from(e.target.files) : [];
            if (list.length) handleFiles(list);
            e.target.value = '';
          }}
        />
      </div>
      {rejectionMessages.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {rejectionMessages.map((msg) => (
            <li key={msg} className="text-xs text-destructive">
              {msg}
            </li>
          ))}
        </ul>
      ) : null}
      {files.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${f.size}-${f.lastModified}`}
              className="flex items-center justify-between gap-2 rounded bg-secondary px-2 py-1 text-xs text-foreground"
            >
              <span className="min-w-0 truncate" title={f.name}>
                {material.name}: {f.name}
              </span>
              <button
                type="button"
                aria-label={`Remove ${f.name} from ${material.name}`}
                onClick={() => onFileRemove(material.id, idx)}
                className="shrink-0 text-muted-foreground hover:text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={`mt-2 text-xs ${invalid ? 'text-destructive' : 'text-muted-foreground'}`}>
          At least one file is required for this material.
        </p>
      )}
    </div>
  );
}
